import { useLanguage } from '../i18n/i18n';
import { isoDay, type Session } from './api';

export function parseDay(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(s: string, n: number): string {
  const d = parseDay(s);
  d.setDate(d.getDate() + n);
  return isoDay(d);
}

export function addMonths(s: string, n: number): string {
  const d = parseDay(s);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return isoDay(d);
}

export function weekday(s: string): number {
  const w = parseDay(s).getDay();
  return w === 0 ? 7 : w;
}

export function mondayOf(s: string): string {
  return addDays(s, 1 - weekday(s));
}

export function monthStart(s: string): string {
  const d = parseDay(s);
  return isoDay(new Date(d.getFullYear(), d.getMonth(), 1));
}

export function monthEnd(s: string): string {
  const d = parseDay(s);
  return isoDay(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export function today(): string {
  return isoDay(new Date());
}

export function shortTime(t: string | null): string | null {
  return t ? t.slice(0, 5) : null;
}

export function sortSessions(list: Session[]): Session[] {
  return [...list].sort((a, b) => a.day.localeCompare(b.day) || (a.startTime ?? '99').localeCompare(b.startTime ?? '99'));
}

export function byDay(list: Session[]): Map<string, Session[]> {
  const map = new Map<string, Session[]>();
  for (const s of sortSessions(list)) {
    const arr = map.get(s.day);
    if (arr) arr.push(s);
    else map.set(s.day, [s]);
  }
  return map;
}

const SHORT_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function parseRepeat(repeat: string | null): { days: number[]; until: string } | null {
  if (!repeat) return null;
  const m = /^(.+) until (\d{4}-\d{2}-\d{2})$/.exec(repeat);
  if (!m) return null;
  const days = m[1].split(' and ').map((n) => SHORT_NAMES.indexOf(n.trim()) + 1).filter((n) => n > 0);
  return days.length ? { days, until: m[2] } : null;
}

export function useDates() {
  const { lang } = useLanguage();
  const locale = lang === 'en' ? 'en-GB' : lang;
  const fmt = (s: string, o: Intl.DateTimeFormatOptions) => parseDay(s).toLocaleDateString(locale, o);
  const thisYear = new Date().getFullYear();
  const withYear = (s: string) => parseDay(s).getFullYear() !== thisYear;
  return {
    locale,
    long: (s: string) => fmt(s, { weekday: 'long', day: 'numeric', month: 'long', year: withYear(s) ? 'numeric' : undefined }),
    medium: (s: string) => fmt(s, { weekday: 'short', day: 'numeric', month: 'short', year: withYear(s) ? 'numeric' : undefined }),
    short: (s: string) => fmt(s, { day: 'numeric', month: 'short', year: withYear(s) ? 'numeric' : undefined }),
    month: (s: string) => fmt(s, { month: 'long', year: 'numeric' }),
    dayNumber: (s: string) => fmt(s, { day: 'numeric' }),
    weekdayShort: (n: number) => fmt(addDays('2024-01-01', n - 1), { weekday: 'short' }),
    weekdayLong: (n: number) => fmt(addDays('2024-01-01', n - 1), { weekday: 'long' }),
    dayList: (days: number[]) => new Intl.ListFormat(locale, { type: 'conjunction' }).format(
      [...days].sort((a, b) => a - b).map((n) => fmt(addDays('2024-01-01', n - 1), { weekday: 'short' })),
    ),
  };
}
